import { GameEngine } from '@color-dots/game';
import { CanvasRenderer } from './game/CanvasRenderer.js';
import { InputController } from './game/InputController.js';
import { BackgroundMusicPlayer } from './game/backgroundMusic.js';
import { InputRouter } from './input/InputRouter.js';
import {
  applyAnimationSetting,
  getStoredSettings,
  resolveGameMode,
  type GameMode,
  type GameSettings,
} from './settings.js';
import { Scene3DApp } from './scene3d/Scene3DApp.js';
import type { ViewportInsets } from './scene3d/CameraRig.js';
import {
  cycleTheme,
  getStoredTheme,
  listenSystemThemeChange,
  themeLabel,
} from './theme.js';
import {
  getCompletedWordLevelIds,
  getTopicCompletionCount,
  getTopicProgress,
  loadWordProgress,
  markWordLevelCompleted,
  setLastWordLevel,
} from './words/progress.js';
import {
  getWordLevelById,
  loadWordManifest,
  loadWordTopic,
  type LoadedWordTopic,
} from './words/loader.js';
import { wordLevelToGameLevel, type WordLevelMeta, type WordTopic } from './words/types.js';
import { WordLabelsLayer } from './words/WordLabelsLayer.js';

export class WordConnectApp {
  private root: HTMLElement;
  private manifest: Awaited<ReturnType<typeof loadWordManifest>> | null = null;
  private topicData: LoadedWordTopic | null = null;
  private currentTopicId: string;
  private currentLevelId = 'W-01';
  private levelMeta: WordLevelMeta | null = null;
  private completedLevels = new Set<string>();
  private settings: GameSettings = getStoredSettings();
  private musicPlayer = new BackgroundMusicPlayer(getStoredSettings().music);
  private mode: GameMode;

  private engine: GameEngine | null = null;
  private canvasRenderer: CanvasRenderer | null = null;
  private scene3d: Scene3DApp | null = null;
  private input2d: InputController | null = null;
  private input3d: InputRouter | null = null;
  private labelsLayer: WordLabelsLayer | null = null;

  private sessionMistakes = 0;
  private winOverlay: HTMLElement | null = null;

  private canvas!: HTMLCanvasElement;
  private undoBtn!: HTMLButtonElement;
  private resetBtn!: HTMLButtonElement;
  private levelLabel!: HTMLElement;
  private levelNav!: HTMLElement;
  private themeBtn!: HTMLButtonElement;
  private rotateLeftBtn!: HTMLButtonElement;
  private rotateRightBtn!: HTMLButtonElement;
  private clearBtn!: HTMLButtonElement;
  private viewBtn: HTMLButtonElement | null = null;

  constructor(root: HTMLElement) {
    this.root = root;
    this.mode = resolveGameMode(this.settings);
    const progress = loadWordProgress();
    this.currentTopicId = progress.lastTopic;
    applyAnimationSetting(this.settings.animations);
    void this.init();
  }

  private async init(): Promise<void> {
    try {
      this.manifest = await loadWordManifest();
      this.showTopicsScreen();
    } catch (error) {
      this.root.innerHTML = `<div class="word-error">Ошибка загрузки режима «Слова»</div>`;
      console.error(error);
    }
  }

  private showTopicsScreen(): void {
    this.destroyGame();

    if (!this.manifest) return;

    const progress = loadWordProgress();
    const lastTopic = this.manifest.topics.find((t) => t.id === progress.lastTopic);

    this.root.innerHTML = `
      <div class="word-shell">
        <header class="word-header">
          <button class="icon-btn" id="back-colors-btn" type="button" title="Color Dots" aria-label="Вернуться в Color Dots">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>
          <div class="word-header-title">
            <h1>Слова</h1>
            <p>Word Connect — соедини переводы</p>
          </div>
          <button class="icon-btn" id="theme-btn" type="button" title="Тема">
            <svg class="theme-icon theme-icon--light" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="4"/>
              <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>
            </svg>
            <svg class="theme-icon theme-icon--dark" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
            </svg>
            <svg class="theme-icon theme-icon--system" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <rect x="2" y="3" width="20" height="14" rx="2"/>
              <path d="M8 21h8M12 17v4"/>
            </svg>
          </button>
        </header>

        ${
          lastTopic?.status === 'available'
            ? `
        <button class="word-quick-start" id="quick-start-btn" type="button">
          <span class="word-quick-start-label">Продолжить</span>
          <span class="word-quick-start-topic">${lastTopic.icon} ${lastTopic.title.ru}</span>
        </button>
        `
            : ''
        }

        <div class="word-filters" role="group" aria-label="Фильтры">
          <button class="word-filter active" type="button" data-filter="all">Все</button>
          <button class="word-filter" type="button" data-filter="new">Новые</button>
          <button class="word-filter" type="button" data-filter="review">Повторение</button>
        </div>

        <div class="word-topic-grid" id="topic-grid"></div>
      </div>
    `;

    this.themeBtn = this.root.querySelector('#theme-btn') as HTMLButtonElement;
    this.setupTheme();

    this.root.querySelector('#back-colors-btn')?.addEventListener('click', () => {
      window.location.hash = '';
      window.location.reload();
    });

    this.root.querySelector('#quick-start-btn')?.addEventListener('click', () => {
      if (lastTopic) void this.startTopic(lastTopic.id);
    });

    const grid = this.root.querySelector('#topic-grid') as HTMLElement;
    this.renderTopicGrid(grid, 'all');

    this.root.querySelectorAll('.word-filter').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.root.querySelectorAll('.word-filter').forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        const filter = (btn as HTMLElement).dataset.filter ?? 'all';
        this.renderTopicGrid(grid, filter);
      });
    });
  }

  private renderTopicGrid(grid: HTMLElement, filter: string): void {
    if (!this.manifest) return;
    grid.innerHTML = '';

    for (const topic of this.manifest.topics) {
      if (filter === 'new' && topic.status !== 'available') continue;
      if (filter === 'new' && getTopicCompletionCount(topic.id) >= topic.levelCount) continue;
      if (filter === 'review') {
        const mistakes = getTopicProgress(topic.id).mistakeWords;
        if (topic.status !== 'available' || mistakes.length === 0) continue;
      }

      grid.appendChild(this.createTopicCard(topic));
    }
  }

  private createTopicCard(topic: WordTopic): HTMLElement {
    const completed = getTopicCompletionCount(topic.id);
    const total = topic.levelCount;
    const pct = total > 0 ? Math.round((completed / total) * 100) : 0;
    const isComingSoon = topic.status === 'coming-soon';

    const card = document.createElement('button');
    card.type = 'button';
    card.className = `word-topic-card${isComingSoon ? ' word-topic-card--soon' : ''}`;
    card.disabled = isComingSoon;
    card.innerHTML = `
      <span class="word-topic-icon" aria-hidden="true">${topic.icon}</span>
      <span class="word-topic-name">${topic.title.ru}</span>
      <span class="word-topic-progress">
        ${isComingSoon ? 'Скоро' : `${completed}/${total} · ${pct}%`}
      </span>
      ${!isComingSoon && total > 0 ? `<span class="word-topic-bar" style="--progress:${pct}%"></span>` : ''}
    `;

    if (!isComingSoon) {
      card.addEventListener('click', () => void this.startTopic(topic.id));
    }

    return card;
  }

  private async startTopic(topicId: string): Promise<void> {
    if (!this.manifest) return;
    const topic = this.manifest.topics.find((t) => t.id === topicId);
    if (!topic || topic.status !== 'available') return;

    // Клик по теме — пользовательский жест: разблокируем и запускаем музыку.
    this.musicPlayer.resume();

    try {
      this.topicData = await loadWordTopic(topic);
      this.currentTopicId = topicId;
      this.completedLevels = getCompletedWordLevelIds(topicId);
      const topicProgress = getTopicProgress(topicId);
      this.currentLevelId = topicProgress.lastLevel;
      this.showGameScreen();
    } catch (error) {
      console.error(error);
    }
  }

  private showGameScreen(): void {
    const is3d = this.mode === '3d';

    this.root.innerHTML = `
      <div class="app-shell app-shell--word ${is3d ? 'app-shell--3d' : ''}">
        <div class="scene-container" id="scene-container">
          <canvas id="game-board" aria-label="Поле Word Connect"></canvas>
          <header class="game-overlay game-overlay--top">
            <button class="overlay-btn" id="back-topics-btn" type="button" title="Темы" aria-label="К темам">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>
            <div class="overlay-level" id="level-label">Загрузка…</div>
            ${is3d ? `
            <button class="overlay-btn" id="view-btn" type="button" title="Сменить обзор" aria-label="Сменить обзор камеры">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="3"/>
                <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z"/>
              </svg>
            </button>
            ` : ''}
            <button class="overlay-btn" id="theme-btn" type="button" title="Тема">
              <svg class="theme-icon theme-icon--light" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="4"/>
                <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>
              </svg>
              <svg class="theme-icon theme-icon--dark" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
              <svg class="theme-icon theme-icon--system" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <rect x="2" y="3" width="20" height="14" rx="2"/>
                <path d="M8 21h8M12 17v4"/>
              </svg>
            </button>
          </header>
          <nav class="level-nav level-nav--overlay level-nav--word" id="level-nav" aria-label="Уровни" hidden></nav>
          <footer class="game-overlay game-overlay--bottom">
            <button class="overlay-action" id="undo-btn" type="button" disabled aria-label="Отменить">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M9 14 4 9l5-5"/>
                <path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5v0a5.5 5.5 0 0 1-5.5 5.5H11"/>
              </svg>
              <span>Отменить</span>
            </button>
            ${is3d ? `
            <button class="overlay-action" id="clear-btn" type="button" aria-label="Очистить">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M3 6h18"/>
                <path d="M8 6V4h8v2"/>
                <path d="M19 6l-1 14H6L5 6"/>
              </svg>
              <span>Очистить</span>
            </button>
            ` : ''}
            <button class="overlay-action" id="reset-btn" type="button" aria-label="Сброс уровня">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                <path d="M3 3v5h5"/>
              </svg>
              <span>Сброс</span>
            </button>
          </footer>
          ${is3d ? `
          <div class="scene-hint" aria-live="polite">Потяни: поворот • Соедини слово с переводом</div>
          <div class="rotate-controls" aria-label="Управление вращением">
            <button class="rotate-btn" id="rotate-left" type="button" aria-label="Вращать против часовой стрелки">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M3 12a9 9 0 1 0 9-9"/>
                <path d="M3 3v5h5"/>
              </svg>
            </button>
            <button class="rotate-btn" id="rotate-right" type="button" aria-label="Вращать по часовой стрелке">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M21 12a9 9 0 1 1-9-9"/>
                <path d="M16 3h5v5"/>
              </svg>
            </button>
          </div>
          ` : `
          <nav class="level-nav level-nav--word" id="level-nav-inline" aria-label="Уровни"></nav>
          `}
        </div>
      </div>
    `;

    this.canvas = this.root.querySelector('#game-board') as HTMLCanvasElement;
    this.canvas.addEventListener('pointerdown', () => this.musicPlayer.resume());
    this.undoBtn = this.root.querySelector('#undo-btn') as HTMLButtonElement;
    this.resetBtn = this.root.querySelector('#reset-btn') as HTMLButtonElement;
    this.levelLabel = this.root.querySelector('#level-label') as HTMLElement;
    this.levelNav = this.root.querySelector(
      is3d ? '#level-nav' : '#level-nav-inline',
    ) as HTMLElement;
    this.themeBtn = this.root.querySelector('#theme-btn') as HTMLButtonElement;

    if (is3d) {
      this.rotateLeftBtn = this.root.querySelector('#rotate-left') as HTMLButtonElement;
      this.rotateRightBtn = this.root.querySelector('#rotate-right') as HTMLButtonElement;
      this.clearBtn = this.root.querySelector('#clear-btn') as HTMLButtonElement;
      this.viewBtn = this.root.querySelector('#view-btn') as HTMLButtonElement;
      this.clearBtn.addEventListener('click', () => this.handleClear());
      this.viewBtn?.addEventListener('click', () => this.handleViewChange());
    }

    this.root.querySelector('#back-topics-btn')?.addEventListener('click', () => {
      this.showTopicsScreen();
    });

    const menuBtn = this.root.querySelector('#menu-btn');
    menuBtn?.addEventListener('click', () => {
      const hidden = this.levelNav.hasAttribute('hidden');
      if (hidden) this.levelNav.removeAttribute('hidden');
      else this.levelNav.setAttribute('hidden', '');
      this.handleResize();
    });

    this.setupTheme();
    this.themeBtn.addEventListener('click', () => {
      cycleTheme();
      this.updateThemeButton();
      this.redrawBoard();
    });

    this.undoBtn.addEventListener('click', () => this.handleUndo());
    this.resetBtn.addEventListener('click', () => this.handleReset());
    window.addEventListener('resize', () => this.handleResize());

    const sceneContainer = this.root.querySelector('#scene-container') as HTMLElement;
    this.labelsLayer = new WordLabelsLayer(sceneContainer, this.canvas, is3d ? '3d' : '2d');

    this.renderLevelNav();
    this.loadLevel(this.currentLevelId);
  }

  private renderLevelNav(): void {
    if (!this.topicData) return;
    this.levelNav.innerHTML = '';

    for (const level of this.topicData.levels.levels) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'level-chip';
      btn.textContent = level.id.replace('W-', '');
      btn.dataset.levelId = level.id;

      if (level.id === this.currentLevelId) btn.classList.add('active');
      if (this.completedLevels.has(level.id)) btn.classList.add('completed');

      btn.addEventListener('click', () => {
        this.loadLevel(level.id);
        if (this.mode === '3d') this.levelNav.setAttribute('hidden', '');
      });
      this.levelNav.appendChild(btn);
    }
  }

  private loadLevel(levelId: string): void {
    if (!this.topicData) return;
    const def = getWordLevelById(this.topicData.levels, levelId);
    if (!def) return;

    this.currentLevelId = levelId;
    setLastWordLevel(this.currentTopicId, levelId);
    this.dismissWinModal();
    this.sessionMistakes = 0;

    this.destroyInput();
    this.destroyRenderer();

    this.levelMeta = wordLevelToGameLevel(def);
    this.engine = new GameEngine(this.levelMeta.level, { requireFullGrid: false });

    if (this.mode === '3d') {
      this.scene3d = new Scene3DApp(this.canvas, { wordMode: true });
      this.scene3d.onAfterRender = () => this.labelsLayer?.update();
      this.scene3d.build(this.engine.getState());
      this.labelsLayer?.attach3d(this.scene3d);
      this.input3d = new InputRouter(
        this.canvas,
        this.scene3d,
        this.engine,
        () => this.syncUi(),
        () => this.handleWin(),
        this.rotateLeftBtn,
        this.rotateRightBtn,
      );
    } else {
      this.canvasRenderer = new CanvasRenderer(this.canvas, { wordMode: true });
      this.labelsLayer?.attach2d(this.canvasRenderer);
      this.input2d = new InputController(
        this.canvas,
        this.canvasRenderer,
        this.engine,
        () => this.syncUi(),
        () => this.handleWin(),
      );
    }

    this.labelsLayer?.setLabels(this.levelMeta.labels, def.size);

    const topicTitle = this.topicData.topic.title.ru;
    const pairCount = def.pairs.length;
    const levelText = `${topicTitle} · ${levelId} · ${def.size}×${def.size} · ${pairCount} пар`;
    this.levelLabel.textContent = levelText;
    this.levelLabel.title = levelText;

    this.renderLevelNav();
    this.handleResize();
    this.syncUi();
  }

  private destroyInput(): void {
    this.input2d?.destroy();
    this.input2d = null;
    this.input3d?.destroy();
    this.input3d = null;
  }

  private destroyRenderer(): void {
    this.scene3d?.destroy();
    this.scene3d = null;
    this.canvasRenderer = null;
  }

  private destroyGame(): void {
    this.destroyInput();
    this.destroyRenderer();
    this.labelsLayer?.destroy();
    this.labelsLayer = null;
    this.engine = null;
    this.levelMeta = null;
  }

  private setupTheme(): void {
    this.updateThemeButton();
    listenSystemThemeChange(() => {
      this.updateThemeButton();
      this.redrawBoard();
    });
    document.addEventListener('themechange', () => {
      this.updateThemeButton();
      this.redrawBoard();
    });
  }

  private updateThemeButton(): void {
    if (!this.themeBtn) return;
    const mode = getStoredTheme();
    this.themeBtn.title = themeLabel(mode);
    this.themeBtn.dataset.themeMode = mode;
  }

  private redrawBoard(): void {
    if (!this.engine) return;
    if (this.mode === '3d' && this.scene3d) {
      this.scene3d.applyTheme();
      this.scene3d.sync(this.engine.getState(), this.input3d?.getActiveColor() ?? null);
      this.labelsLayer?.update();
    } else if (this.canvasRenderer) {
      this.canvasRenderer.render(this.engine.getState(), this.input2d?.getActiveColor() ?? null);
      this.labelsLayer?.update();
    }
  }

  private syncUi(): void {
    if (!this.engine) return;

    if (this.mode === '3d' && this.scene3d) {
      this.scene3d.sync(this.engine.getState(), this.input3d?.getActiveColor() ?? null);
      this.labelsLayer?.update();
    } else if (this.canvasRenderer) {
      this.canvasRenderer.render(this.engine.getState(), this.input2d?.getActiveColor() ?? null);
      this.labelsLayer?.update();
    }

    this.undoBtn.disabled = !this.engine.canUndo();
  }

  private handleResize(): void {
    if (!this.engine) return;

    if (this.mode === '3d' && this.scene3d) {
      this.scene3d.resize(this.getViewportInsets());
      this.scene3d.sync(this.engine.getState(), this.input3d?.getActiveColor() ?? null);
      this.labelsLayer?.update();
      return;
    }

    if (!this.canvasRenderer) return;
    const wrap = this.canvas.parentElement;
    if (!wrap) return;

    const topBarHeight =
      this.root.querySelector('.game-overlay--top')?.getBoundingClientRect().height ?? 0;
    const bottomBarHeight =
      this.root.querySelector('.game-overlay--bottom')?.getBoundingClientRect().height ?? 0;
    const levelNavHeight = this.levelNav?.getBoundingClientRect().height ?? 0;
    const verticalGap = 24;
    const availableHeight =
      wrap.clientHeight - topBarHeight - bottomBarHeight - levelNavHeight - verticalGap;
    const boundedHeight = Math.max(220, availableHeight);
    const maxSize = Math.min(wrap.clientWidth, boundedHeight, 480);

    this.canvasRenderer.resize(this.engine.getState(), maxSize);
    this.canvasRenderer.render(this.engine.getState(), this.input2d?.getActiveColor() ?? null);
    this.labelsLayer?.update();
  }

  private getViewportInsets(): ViewportInsets {
    const topBar = this.root.querySelector('.game-overlay--top');
    const bottomBar = this.root.querySelector('.game-overlay--bottom');
    const nav = this.levelNav;
    const rotate = this.root.querySelector('.rotate-controls');

    let top = topBar?.getBoundingClientRect().height ?? 64;
    if (nav && !nav.hasAttribute('hidden')) {
      top += nav.getBoundingClientRect().height;
    }

    let bottom = bottomBar?.getBoundingClientRect().height ?? 88;
    const rotateRect = rotate?.getBoundingClientRect();
    if (rotateRect) {
      bottom = Math.max(bottom, window.innerHeight - rotateRect.top + 8);
    }

    return { top, bottom };
  }

  private handleViewChange(): void {
    this.scene3d?.cycleView(this.getViewportInsets());
    this.labelsLayer?.update();
  }

  private handleUndo(): void {
    if (!this.engine) return;
    this.engine.undo();
    this.sessionMistakes += 1;
    this.syncUi();
  }

  private handleClear(): void {
    if (!this.engine) return;
    const state = this.engine.getState();
    let cleared = false;
    for (const color of state.paths.keys()) {
      const path = state.paths.get(color);
      if (path && path.length > 0) {
        this.engine.clearPath(color);
        cleared = true;
      }
    }
    if (cleared) this.sessionMistakes += 1;
    this.syncUi();
  }

  private handleReset(): void {
    if (!this.engine) return;
    this.engine.reset();
    this.syncUi();
  }

  private handleWin(): void {
    if (!this.levelMeta || !this.topicData) return;

    const total = this.levelMeta.def.pairs.length;
    const wordIds = this.levelMeta.def.pairs.map((p) => p.pairId);
    const previouslySeen = new Set<string>();

    for (const [levelId, result] of Object.entries(
      getTopicProgress(this.currentTopicId).completed,
    )) {
      if (levelId === this.currentLevelId) continue;
      const def = getWordLevelById(this.topicData.levels, levelId);
      if (def) {
        for (const p of def.pairs) previouslySeen.add(p.pairId);
      }
      void result;
    }

    const newWords = wordIds.filter((id) => !previouslySeen.has(id)).length;

    markWordLevelCompleted(this.currentTopicId, this.currentLevelId, {
      correct: total,
      total,
      mistakes: this.sessionMistakes,
      newWords,
    }, this.sessionMistakes > 0 ? wordIds : []);

    this.completedLevels.add(this.currentLevelId);
    this.renderLevelNav();
    this.showWinModal(total, this.sessionMistakes, newWords);
  }

  private getNextLevelId(): string | null {
    if (!this.topicData) return null;
    const levels = this.topicData.levels.levels;
    const index = levels.findIndex((l) => l.id === this.currentLevelId);
    return levels[index + 1]?.id ?? null;
  }

  private showWinModal(correct: number, mistakes: number, newWords: number): void {
    this.dismissWinModal();

    const nextLevelId = this.getNextLevelId();

    const overlay = document.createElement('div');
    overlay.className = 'win-overlay';
    overlay.innerHTML = `
      <div class="win-modal win-modal--words" role="dialog" aria-modal="true">
        <div class="win-icon" aria-hidden="true">✓</div>
        <h2>Уровень пройден!</h2>
        <div class="word-result-stats">
          <p>Правильно: <strong>${correct}/${correct}</strong></p>
          <p>Ошибок: <strong>${mistakes}</strong></p>
          <p>Новых слов: <strong>${newWords}</strong></p>
        </div>
        <div class="actions">
          ${
            nextLevelId
              ? `<button class="action-btn primary" id="next-level-btn" type="button">Дальше</button>`
              : `<button class="action-btn primary" id="close-win-btn" type="button">К темам</button>`
          }
          <button class="action-btn" id="replay-btn" type="button">Повторить</button>
        </div>
      </div>
    `;

    overlay.querySelector('#next-level-btn')?.addEventListener('click', () => {
      this.dismissWinModal();
      if (nextLevelId) this.loadLevel(nextLevelId);
    });

    overlay.querySelector('#close-win-btn')?.addEventListener('click', () => {
      this.dismissWinModal();
      this.showTopicsScreen();
    });

    overlay.querySelector('#replay-btn')?.addEventListener('click', () => {
      this.dismissWinModal();
      this.handleReset();
    });

    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) this.dismissWinModal();
    });

    document.body.appendChild(overlay);
    this.winOverlay = overlay;
  }

  private dismissWinModal(): void {
    this.winOverlay?.remove();
    this.winOverlay = null;
  }
}
